import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { getAllFunnels } from 'API/linkedin-funnel';
import { FunnelTypes } from 'features/co-pilot/co-pilot.types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createWizardCampaign, WizardApiError } from '../../campaign-wizard.api';
import { WizardState } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import {
  MiSpinner,
  WizGradientButton,
  WizInput,
  WizLabel,
  WizLink,
  WizSwitch,
} from '../../components/wizard-ui';
import WizSelect from './wiz-select';

interface Props {
  open: boolean;
  onClose: () => void;
  accountId: string;
  briefing: string;
  /** Eine Kopie wurde angelegt: der Wizard übernimmt sie. */
  onCreated: (campaignId: string, state: WizardState) => void;
}

interface CampaignOption {
  id: string;
  name: string;
}

/** „Vorhandene Kampagne als Vorlage nutzen“: kopiert Briefing, Kriterien und Ansprache einer Recruiting-Kampagne. */
const CloneDialog: React.FC<Props> = ({ open, onClose, accountId, briefing, onCreated }) => {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState<CampaignOption[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState('');
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [cloneContacts, setCloneContacts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCampaigns(null);
    setLoadError(false);
    setError(null);
    void getAllFunnels(FunnelTypes.ACTIVE_SOURCING_CO_PILOT).then(res => {
      if (cancelled) return;
      if (!res || res.error || !Array.isArray(res.campaigns)) {
        setLoadError(true);
        setCampaigns([]);
        return;
      }
      setCampaigns(
        (res.campaigns as Array<{ _id: string; name: string }>).map(c => ({
          id: c._id,
          name: c.name,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const pick = (id: string) => {
    setSelected(id);
    if (!nameEdited) {
      const c = campaigns?.find(x => x.id === id);
      setName(c ? `${c.name} ${t('wizJobCloneNameSuffix')}` : '');
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await createWizardCampaign({
        name: name.trim(),
        accountId,
        briefing,
        cloneFromCampaignId: selected,
        cloneContacts,
      });
      onCreated(res.campaignId, res.state);
      onClose();
    } catch (e) {
      const err = e as WizardApiError;
      setError(err.nameTaken ? t('wizNameTaken') : `${t('wizJobCloneError')} ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const ready = !!selected && name.trim().length > 0 && !!accountId;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>{t('wizJobCloneTitle')}</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px !important' }}
      >
        <div style={{ fontSize: 15, color: wz.muted }}>{t('wizJobCloneIntro')}</div>
        {campaigns === null ? (
          <MiSpinner size={24} />
        ) : loadError ? (
          <div style={{ fontSize: 14, color: wz.redFg }}>{t('wizJobCloneLoadError')}</div>
        ) : campaigns.length === 0 ? (
          <div style={{ fontSize: 14, color: wz.muted }}>{t('wizJobCloneNone')}</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <WizLabel>{t('wizJobCloneSelect')}</WizLabel>
              <WizSelect
                value={selected}
                options={campaigns.map(c => ({ value: c.id, label: c.name }))}
                placeholder={t('wizJobCloneSelectPlaceholder')}
                onChange={pick}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <WizLabel>{t('wizJobNameLabel')}</WizLabel>
              <WizInput
                value={name}
                onChange={v => {
                  setName(v);
                  setNameEdited(true);
                }}
              />
            </div>
            <WizSwitch
              on={cloneContacts}
              onToggle={() => setCloneContacts(v => !v)}
              label={t('wizJobCloneContacts')}
            />
          </>
        )}
        {error ? <div style={{ fontSize: 13, color: wz.redFg }}>{error}</div> : null}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 20px', gap: '16px' }}>
        <WizLink onClick={onClose} color={wz.text} disabled={busy}>
          {t('wizJobCloneCancel')}
        </WizLink>
        <WizGradientButton onClick={() => void submit()} disabled={!ready} loading={busy}>
          {t('wizJobCloneConfirm')}
        </WizGradientButton>
      </DialogActions>
    </Dialog>
  );
};

export default CloneDialog;
